import { useMemo } from 'react';
import classNames from 'classnames';
import { Icon } from 'components/icons/Icon';
import { getValidationSummary } from 'utils/ValidationUtils';
import { ValidationIssue } from '@decisions/server/DecisionsFramework/ValidationIssue';
import { BreakLevel } from '@decisions/server/DecisionsFramework/BreakLevel';
import { useHoverValidation } from 'components/inputs/Validation/hooks/useHoverValidation';
import { Popper } from 'react-popper';
import { createPortal } from 'react-dom';
import { Fade } from '@material-ui/core';
import { CalloutCaret } from 'components/layout/CalloutCaret';
import { getMaxZIndex } from 'utils/DomUtils';
import { ValidationList } from 'components/inputs/Validation/ValidationList';
import './ValidationIcon.less';

export interface IValidationIconProps {
  className?: string;
  validationIssues: ValidationIssue[];
}

const getClassName = (validationLevel: BreakLevel, className?: string) =>
  classNames(
    'rule-validation',
    {
      'dp-validation-icon': validationLevel === BreakLevel.Fatal,
      'dp-warning-icon': validationLevel === BreakLevel.Warning,
    },
    className
  );

/**
 * Validation icon indicates that there are validation issues.
 * Displays error icon for "BreakLevel.Fatal" and warning icon for "BreakLevel.Warning".
 * If there are issues for different break level, should be displayed icon for the worst one.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ValidationIcon({ validationIssues, className }: IValidationIconProps) {
  const summary = getValidationSummary(validationIssues);
  const { inputRef, onShowValidation, onHideValidation, valInputAttributes, toolTipId } = useHoverValidation(summary);
  const zIndex = useMemo(() => getMaxZIndex(), []);

  if (summary.worstLevel === BreakLevel.Ignored) return null;

  return (
    <>
      <Icon
        className={getClassName(summary.worstLevel, className)}
        size={'large'}
        onMouseOver={onShowValidation}
        onMouseLeave={onHideValidation}
        {...valInputAttributes}
      />
      {createPortal(
        <Popper
          placement="bottom-start"
          referenceElement={inputRef}
          eventsEnabled={true}
          modifiers={{
            offset: {
              offset: `-18px, 14px`,
            },
          }}
        >
          {({ ref, style, placement, arrowProps }) => (
            <Fade timeout={200} in={inputRef != null}>
              <ValidationList
                ref={ref}
                id={toolTipId}
                className={'dp-validation-icon-tooltip'}
                style={{ ...style, ...{ backgroundColor: 'white', zIndex } }}
                role={'tooltip'}
                reasons={summary.reasons}
                levels={summary.levels}
                worstLevel={summary.worstLevel}
              >
                <CalloutCaret {...arrowProps} placement={placement} style={{ ...arrowProps.style }} />
              </ValidationList>
            </Fade>
          )}
        </Popper>,
        document.body
      )}
    </>
  );
}
